import { useState, useEffect, useCallback } from 'react';
import { api } from '@/App';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { toast } from 'sonner';
import { 
  ClipboardList, Plus, Send, Eye, Trash2, FileText, 
  RefreshCw, Settings2, Search, Printer, FileDown, ChevronDown,
  Filter, ArrowUpDown, Edit, Check, X
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EnterpriseToolbar, EnterpriseTableHead, StatusBadge, EmptyState, SelectCheckbox, RowActions } from '@/components/EnterpriseTable';

export default function PurchaseOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Dialogs
  const [poDialog, setPODialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  
  // Form
  const [poForm, setPOForm] = useState({
    vendor_id: '', payment_terms: 30, delivery_date: '', notes: '',
    items: [{ item_name: '', quantity: 1, unit: 'nos', unit_price: 0, tax_rate: 18 }]
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [posRes, vendorsRes] = await Promise.all([
        api.get('/purchase-orders'),
        api.get('/vendors')
      ]);
      setOrders(posRes.data);
      setVendors(vendorsRes.data);
    } catch (err) {
      toast.error('Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreatePO = async () => {
    try {
      await api.post('/purchase-orders', poForm);
      toast.success('Purchase order created successfully');
      setPODialog(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create purchase order');
    }
  };

  const handleSendPO = async (poId) => {
    try {
      await api.put(`/purchase-orders/${poId}/send`);
      toast.success('PO sent to vendor');
      fetchData();
    } catch (err) {
      toast.error('Failed to send PO');
    }
  };

  const resetForm = () => {
    setSelectedPO(null);
    setPOForm({
      vendor_id: '', payment_terms: 30, delivery_date: '', notes: '',
      items: [{ item_name: '', quantity: 1, unit: 'nos', unit_price: 0, tax_rate: 18 }]
    });
  };

  const addItem = () => {
    setPOForm({
      ...poForm,
      items: [...poForm.items, { item_name: '', quantity: 1, unit: 'nos', unit_price: 0, tax_rate: 18 }]
    });
  };

  const updateItem = (idx, field, value) => {
    const newItems = [...poForm.items];
    newItems[idx][field] = value;
    setPOForm({ ...poForm, items: newItems });
  };

  const removeItem = (idx) => {
    if (poForm.items.length > 1) {
      setPOForm({ ...poForm, items: poForm.items.filter((_, i) => i !== idx) });
    }
  };

  const calculateTotal = (items) => {
    return items?.reduce((sum, item) => {
      const subtotal = item.quantity * item.unit_price;
      const tax = subtotal * (item.tax_rate || 0) / 100;
      return sum + subtotal + tax;
    }, 0) || 0;
  };

  const getVendorName = (vendorId) => vendors.find(v => v.id === vendorId)?.name || vendorId;

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredOrders.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredOrders.map(o => o.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const filteredOrders = orders.filter(o => 
    o.po_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    o.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleViewSelected = () => {
    const po = orders.find(o => o.id === selectedIds[0]);
    if (po) {
      setSelectedPO(po);
      setViewDialog(true);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-12 w-12 rounded-full border-4 border-emerald-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-slate-50" data-testid="purchase-orders-page">
      {/* Enterprise Toolbar */}
      <EnterpriseToolbar
        title="Purchase Orders"
        totalCount={filteredOrders.length}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onRefresh={fetchData}
        onAdd={() => { resetForm(); setPODialog(true); }}
        onView={handleViewSelected}
        onPrint={() => window.print()}
        onExport={() => toast.info('Export functionality coming soon')}
        selectedCount={selectedIds.length}
        addLabel="New PO"
        customActions={[
          { 
            label: 'Send to Vendor', 
            icon: Send,
            onClick: () => {
              if (selectedIds.length === 1) {
                const po = orders.find(o => o.id === selectedIds[0]);
                if (po?.status === 'draft') handleSendPO(po.id);
              }
            },
            disabled: selectedIds.length !== 1
          }
        ]}
      />

      {/* Data Table */}
      <div className="flex-1 overflow-auto">
        <Card className="border-slate-200 shadow-sm rounded-none border-x-0">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-100 hover:bg-slate-100">
                  <TableHead className="w-6 px-1.5 py-1">
                    <SelectCheckbox 
                      checked={selectedIds.length === filteredOrders.length && filteredOrders.length > 0}
                      indeterminate={selectedIds.length > 0 && selectedIds.length < filteredOrders.length}
                      onCheckedChange={toggleSelectAll}
                    />
                  </TableHead>
                  <EnterpriseTableHead sortable filterable className="w-10">S.No</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>PO Number</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>PO Date</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>Supplier Name</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>Items</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable className="text-right">Total Amount</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>PO Status</EnterpriseTableHead>
                  <EnterpriseTableHead className="w-24">Actions</EnterpriseTableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map((po, index) => (
                  <TableRow 
                    key={po.id} 
                    className={`hover:bg-slate-50 ${selectedIds.includes(po.id) ? 'bg-blue-50' : ''}`}
                  >
                    <TableCell className="py-1 px-1.5">
                      <SelectCheckbox 
                        checked={selectedIds.includes(po.id)}
                        onCheckedChange={() => toggleSelect(po.id)}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs text-slate-500 py-1 px-1.5">{index + 1}</TableCell>
                    <TableCell className="font-mono text-xs font-medium text-slate-800 py-1 px-1.5">{po.po_number}</TableCell>
                    <TableCell className="text-xs text-slate-600 py-1 px-1.5">
                      {new Date(po.created_at).toLocaleDateString('en-IN', { 
                        day: '2-digit', month: 'short', year: 'numeric' 
                      })}
                    </TableCell>
                    <TableCell className="text-xs text-slate-800 py-1 px-1.5">{po.vendor_name || getVendorName(po.vendor_id)}</TableCell>
                    <TableCell className="text-xs text-slate-600 py-1 px-1.5">{po.items?.length || 0} items</TableCell>
                    <TableCell className="text-right font-mono text-xs font-semibold text-slate-800 py-1 px-1.5">
                      ₹{(po.total_amount || calculateTotal(po.items)).toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell className="py-1 px-1.5">
                      <StatusBadge status={po.status} />
                    </TableCell>
                    <TableCell className="py-1 px-1.5">
                      <RowActions
                        onView={() => { setSelectedPO(po); setViewDialog(true); }}
                        customActions={[
                          ...(po.status === 'draft' ? [{
                            icon: Send,
                            onClick: () => handleSendPO(po.id),
                            title: 'Send to Vendor',
                            className: 'text-blue-500'
                          }] : [])
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {filteredOrders.length === 0 && (
              <EmptyState
                icon={ClipboardList}
                title="No Purchase Orders Found"
                description={searchTerm ? 'Try adjusting your search criteria' : 'Create your first purchase order to get started'}
                action={
                  <Button onClick={() => { resetForm(); setPODialog(true); }} className="bg-emerald-600 hover:bg-emerald-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Purchase Order
                  </Button>
                }
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create PO Dialog */}
      <Dialog open={poDialog} onOpenChange={setPODialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">New Purchase Order</DialogTitle>
          </DialogHeader>
          <div className="grid gap-6 py-4">
            {/* Header Section */}
            <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
              <div className="grid gap-2">
                <Label className="text-xs font-medium text-slate-500 uppercase">Vendor *</Label>
                <Select value={poForm.vendor_id} onValueChange={(v) => setPOForm({...poForm, vendor_id: v})}>
                  <SelectTrigger className="bg-white"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                  <SelectContent>
                    {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label className="text-xs font-medium text-slate-500 uppercase">Payment Terms</Label>
                <Select value={String(poForm.payment_terms)} onValueChange={(v) => setPOForm({...poForm, payment_terms: Number(v)})}>
                  <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Immediate</SelectItem>
                    <SelectItem value="7">7 Days</SelectItem>
                    <SelectItem value="15">15 Days</SelectItem>
                    <SelectItem value="30">30 Days</SelectItem>
                    <SelectItem value="45">45 Days</SelectItem>
                    <SelectItem value="60">60 Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label className="text-xs font-medium text-slate-500 uppercase">Expected Delivery</Label>
                <Input 
                  type="date"
                  value={poForm.delivery_date}
                  onChange={(e) => setPOForm({...poForm, delivery_date: e.target.value})}
                  className="bg-white"
                />
              </div>
            </div>
            
            {/* Items Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Line Items</Label>
                <Button variant="outline" size="sm" onClick={addItem}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Line
                </Button>
              </div>
              
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-100">
                      <TableHead className="w-12 text-center">#</TableHead>
                      <TableHead>Item Description</TableHead>
                      <TableHead className="w-24">Qty</TableHead>
                      <TableHead className="w-28">Unit</TableHead>
                      <TableHead className="w-32">Unit Price</TableHead>
                      <TableHead className="w-24">Tax %</TableHead>
                      <TableHead className="w-32 text-right">Amount</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {poForm.items.map((item, idx) => {
                      const amount = item.quantity * item.unit_price * (1 + item.tax_rate / 100);
                      return (
                        <TableRow key={idx}>
                          <TableCell className="text-center text-slate-500">{idx + 1}</TableCell>
                          <TableCell>
                            <Input 
                              placeholder="Enter item name/description"
                              value={item.item_name}
                              onChange={(e) => updateItem(idx, 'item_name', e.target.value)}
                              className="border-0 bg-transparent focus-visible:ring-0 px-0"
                            />
                          </TableCell>
                          <TableCell>
                            <Input 
                              type="number"
                              value={item.quantity}
                              onChange={(e) => updateItem(idx, 'quantity', Number(e.target.value))}
                              className="text-center"
                            />
                          </TableCell>
                          <TableCell>
                            <Select value={item.unit} onValueChange={(v) => updateItem(idx, 'unit', v)}>
                              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="nos">Nos</SelectItem>
                                <SelectItem value="kg">Kg</SelectItem>
                                <SelectItem value="l">Ltr</SelectItem>
                                <SelectItem value="pcs">Pcs</SelectItem>
                                <SelectItem value="box">Box</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Input 
                              type="number"
                              value={item.unit_price}
                              onChange={(e) => updateItem(idx, 'unit_price', Number(e.target.value))}
                            />
                          </TableCell>
                          <TableCell>
                            <Select value={String(item.tax_rate)} onValueChange={(v) => updateItem(idx, 'tax_rate', Number(v))}>
                              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="0">0%</SelectItem>
                                <SelectItem value="5">5%</SelectItem>
                                <SelectItem value="12">12%</SelectItem>
                                <SelectItem value="18">18%</SelectItem>
                                <SelectItem value="28">28%</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            ₹{amount.toFixed(2)}
                          </TableCell>
                          <TableCell>
                            {poForm.items.length > 1 && (
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => removeItem(idx)}>
                                <X className="h-4 w-4 text-red-500" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Notes */}
            <div className="grid gap-2">
              <Label className="text-xs font-medium text-slate-500 uppercase">Notes / Instructions</Label>
              <Textarea 
                value={poForm.notes}
                onChange={(e) => setPOForm({...poForm, notes: e.target.value})}
                placeholder="Delivery instructions, special requirements..."
                className="min-h-[80px]"
              />
            </div>

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-72 space-y-2 p-4 bg-slate-50 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Subtotal:</span>
                  <span className="font-mono">₹{poForm.items.reduce((s, i) => s + i.quantity * i.unit_price, 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Tax:</span>
                  <span className="font-mono">₹{poForm.items.reduce((s, i) => s + i.quantity * i.unit_price * i.tax_rate / 100, 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-semibold border-t pt-2">
                  <span>Total:</span>
                  <span className="font-mono text-emerald-600">₹{calculateTotal(poForm.items).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPODialog(false)}>Cancel</Button>
            <Button onClick={handleCreatePO} className="bg-emerald-600 hover:bg-emerald-700">
              Create Purchase Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View PO Dialog */}
      <Dialog open={viewDialog} onOpenChange={setViewDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-xl font-semibold">Purchase Order: {selectedPO?.po_number}</DialogTitle>
              <StatusBadge status={selectedPO?.status} />
            </div>
          </DialogHeader>
          {selectedPO && (
            <div className="space-y-6">
              {/* Header Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg text-sm">
                <div>
                  <span className="text-slate-500">Vendor:</span>
                  <p className="font-medium">{selectedPO.vendor_name || getVendorName(selectedPO.vendor_id)}</p>
                </div>
                <div>
                  <span className="text-slate-500">Date:</span>
                  <p className="font-medium">{new Date(selectedPO.created_at).toLocaleDateString('en-IN')}</p>
                </div>
                <div>
                  <span className="text-slate-500">Payment Terms:</span>
                  <p className="font-medium">{selectedPO.payment_terms || 30} days</p>
                </div>
                <div>
                  <span className="text-slate-500">Status:</span>
                  <p className="font-medium capitalize">{selectedPO.status?.replace('_', ' ')}</p>
                </div>
              </div>
              
              {/* Items */}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-100">
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-center">Qty</TableHead>
                      <TableHead className="text-right">Unit Price</TableHead>
                      <TableHead className="text-center">Tax</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedPO.items?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-slate-500">{idx + 1}</TableCell>
                        <TableCell className="font-medium">{item.item_name}</TableCell>
                        <TableCell className="text-center">{item.quantity} {item.unit}</TableCell>
                        <TableCell className="text-right font-mono">₹{item.unit_price}</TableCell>
                        <TableCell className="text-center">{item.tax_rate}%</TableCell>
                        <TableCell className="text-right font-mono font-medium">
                          ₹{((item.quantity * item.unit_price) * (1 + item.tax_rate / 100)).toLocaleString('en-IN')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Total */}
              <div className="flex justify-end">
                <div className="text-right">
                  <span className="text-slate-500 text-sm">Grand Total:</span>
                  <p className="text-2xl font-bold text-emerald-600 font-mono">
                    ₹{(selectedPO.total_amount || calculateTotal(selectedPO.items)).toLocaleString('en-IN')}
                  </p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialog(false)}>Close</Button>
            {selectedPO?.status === 'draft' && (
              <Button onClick={() => { handleSendPO(selectedPO.id); setViewDialog(false); }} className="bg-blue-600 hover:bg-blue-700">
                <Send className="h-4 w-4 mr-2" />
                Send to Vendor
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
